import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import history from 'State/history';
import { dashboardUri } from 'Consts/clientUris';
import useScrollTopOnMount from 'Hooks/useScrollTopOnMount';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import Button1 from 'Components/ComponentLibrary/Components/Buttons/Button1';
import fr from 'facet-rest';
import WelcomeLayout from 'Views/Onboarding/Pages/Survey/SurveyStep/StepLibrary/Welcome/Layout';
import WelcomeParagraphs from 'Views/Onboarding/Pages/Survey/SurveyStep/Components/WelcomeParagraphs';
import { staticAppUrl } from 'Consts/sharedUris';
import useDarkHeaderTheme from 'Views/Onboarding/Hooks/useDarkHeaderTheme';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import SimplePicker from 'Helpers/SimplePicker';
import PrimaryActionButtonPicker
  from 'Views/Onboarding/Pages/Survey/SurveyStep/StepLibrary/OnboardingScheduleMeeting/PrimaryActionButtonPicker';
import { FDBox, FDContentContainer, FDStack } from '@facet-wealth/design';
import WelcomeLowerContentWrapper
  from 'Views/Onboarding/Pages/Survey/SurveyStep/Components/WelcomeLowerContent/WelcomeLowerContentWrapper';
import Typography from 'Components/UI/Typography';

const ICON_CONTAINER_SIZE = 160;

const styles = (theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  iconWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.facet.accentBlue21,
    width: ICON_CONTAINER_SIZE,
    height: ICON_CONTAINER_SIZE,
    borderRadius: '100%',
    fontSize: theme.typography.h1.fontSize,
    marginBottom: theme.spacing(4),
  },
  icon: {
    height: ICON_CONTAINER_SIZE / 2,
    width: 'auto',
  },
  title: {
    marginBottom: theme.spacing(4),
  },
  message: {},
  gotoDashboardButton: {
    marginBottom: theme.spacing(2),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
  welcomeParagraph: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
    fontWeight: theme.typography.fontWeightRegular,
  },
  image: {
    backgroundImage: `url(${staticAppUrl}/image/onboarding/first-meeting-gradient.png)`,
  },
  skipButton: {
    textDecoration: 'underline',
    width: 'fit-content',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

const CompletedOnboardingLayout = ({
  className,
  classes,
}) => {
  const { resource } = useMetaLayoutContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  useScrollTopOnMount();
  useDarkHeaderTheme();

  const goToDashboard = () => {
    history.push(dashboardUri);
  };

  const onSkipAndScheduleLaterClick = () => {
    logStandardizedEvent(categories.digitalOnboarding, eventTypes.clickButton, 'skipping', { eventProperties: { description: 'skip & schedule later' } });
    goToDashboard();
  };

  return (
    <WelcomeLayout
      classes={{
        root: classNames(className, classes.root),
        imageCardContainer: classes.image,
      }}
      showFooter={false}
    >
      <WelcomeLowerContentWrapper className={classes.content}>
        <FDBox paddingY={2}>
          <Typography
            variant={'h5'}
            color={'primaryGray'}
            weight={'bold'}
          >
            {resource.attr('subtitle')}
          </Typography>
        </FDBox>
        <WelcomeParagraphs
          className={classes.welcomeParagraph}
          paragraphs={resource.attr('descriptions').map((description) => {
            return { text: description };
          })}
        />
        <FDContentContainer maxWidth={'xs'}>
          <FDStack spacing={2} direction={'column'} alignItems={'center'} justifyContent="flex-start">
            <PrimaryActionButtonPicker
              resource={resource}
            />
            <SimplePicker
              resource={resource}
              relationship={fr.relationships.scheduleMeetingWithPlanner.name}
              hasRelationship={() => (
                <Button1
                  onClick={onSkipAndScheduleLaterClick}
                  className={classes.skipButton}
                  test="skip-button"
                >
                  {'Skip & schedule later'}
                </Button1>
              )}
            />
          </FDStack>
        </FDContentContainer>
      </WelcomeLowerContentWrapper>
    </WelcomeLayout>
  );
};

CompletedOnboardingLayout.defaultProps = {
  className: '',
};

CompletedOnboardingLayout.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CompletedOnboardingLayout);
